import React, { Fragment, useState, useEffect, memo, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IRouteName, injectRouter } from '../../interface/route';
import Controller from '../../controllers/siteobject';
import args, { equals as ArgsEquals } from './arguments';
import type { ISiteObjectControllerProps } from '../../controllers/siteobject/interface';
import { injectLog, injectQueryURL } from '../../interface/util';
import { DataCheckResult } from '../../controllers/interface';
import { injectConfigAPI } from '../../interface/config';
import { sessionHook } from '@monofe/apiservice';
import util from '../util';
import { injectToast } from '../../controllers/toast/interface';

const DISPLAY_TOAST_DURATION = 6000;

function SiteObjectRoute(props: RouteComponentProps<{}>): JSX.Element {
    const query = injectQueryURL();
    const router = injectRouter();
    const configAPI = injectConfigAPI();
    const log = injectLog();
    const session = sessionHook.useActiveSession();

    const showToast = useCallback((text: string, type: 'success' | 'error') => {
        const toaster = injectToast();
        toaster.show({
            text,
            type,
        });
    }, []);

    const [controller, setController] = useState<ISiteObjectControllerProps>({
        route: args(props, query, configAPI),
        didRequestSiteObject: function (this: ISiteObjectControllerProps, id: number) {
            const newRoute = {
                ...controller.route,
                siteObjectId: id,
            };

            Promise.all([Controller.canLoad(newRoute), Controller.canRender(newRoute)])
                .then(function ([canLoad, canRender]) {
                    const canPush =
                        navigator.onLine || canLoad === DataCheckResult.DataAvailableOffline;

                    if (canPush && canRender) {
                        router.push(IRouteName.SiteObject, props.history, newRoute);
                    } else {
                        showToast(
                            !canPush
                                ? 'Unable to launch without internet connection'
                                : '`Unable to display content',
                            'error',
                        );
                    }
                })
                .catch(error => log.error(error.toString()));
        },
        didRequestBack: function (this: ISiteObjectControllerProps) {
            if (!router.pop(props.history, controller.route)) {
                /* if we fail to go back, reset the app & go to Launch route */
                router.push(IRouteName.Launch, props.history, controller.route, { replace: true });
            }
        },
    });

    useEffect(() => {
        const newRoute = args(props, query, configAPI);
        setController(controllerIn => ({ ...controllerIn, route: newRoute }));
    }, [props]);

    /* no active session, /launch has not been triggered, force page reload & session initialization */
    if (!session) {
        props.history.replace('/');
    }

    return (
        <Fragment key="SiteObjectRoute">
            <Controller.renderer key="SiteObjectRoute" {...controller} />
        </Fragment>
    );
}

const propsEqual = (prev: RouteComponentProps<{}>, next: RouteComponentProps<{}>) => {
    return (
        prev.location.search === next.location.search &&
        prev.location.pathname === next.location.pathname
    );
};

export default memo(SiteObjectRoute, propsEqual);
